import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AdminResend() {
  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState({});
  
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadTimezones();
    loadHistory();
  }, []);

  const loadTimezones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/timezones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimezones(response.data.timezones);
    } catch (err) {
      console.error('Erreur chargement timezones:', err);
      enqueueSnackbar('Erreur lors du chargement des timezones', { variant: 'error' });
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/send-history?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.history);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      enqueueSnackbar('Erreur lors du chargement de l\'historique', { variant: 'error' });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleResend = async () => {
    if (!selectedTimezone) {
      enqueueSnackbar('Veuillez sélectionner une timezone', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/resend-timezone`,
        {
          timezone: selectedTimezone,
          date: selectedDate
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      enqueueSnackbar(
        `✅ ${response.data.sent} messages envoyés sur ${response.data.subscribers} abonnés`,
        { variant: 'success' }
      );
      
      // Recharger l'historique après 2 secondes
      setTimeout(() => loadHistory(), 2000);
    } catch (err) {
      console.error('Erreur renvoi:', err);
      const errorMsg = err.response?.data?.error || 'Erreur lors du renvoi des messages';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const totalSubscribers = timezones.reduce((sum, tz) => sum + tz.count, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            🔄 Renvoi de messages
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Renvoyer les éphémérides à tous les abonnés d'une timezone
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Formulaire de renvoi */}
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon color="primary" />
                Paramètres d'envoi
              </Typography>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={selectedTimezone}
                  label="Timezone"
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Sélectionner une timezone</em>
                  </MenuItem>
                  {timezones.map((tz) => (
                    <MenuItem key={tz.timezone} value={tz.timezone}>
                      {tz.timezone} ({tz.count} abonnés)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ mt: 2 }}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                helperText="Date de l'éphéméride à renvoyer"
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleResend}
                disabled={loading || !selectedTimezone}
                sx={{ mt: 3 }}
              >
                {loading ? 'Envoi en cours...' : 'Renvoyer les messages'}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {result && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    ✅ {result.message}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Timezone: {result.timezone} | Date: {result.date}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Envoyés: {result.sent}/{result.subscribers}
                  </Typography>
                </Alert>
              )}

              <Alert severity="info" icon={<ScheduleIcon />} sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note :</strong> Le renvoi supprime le verrou existant et relance l'envoi pour tous les abonnés actifs de cette timezone.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistiques des timezones */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 Statistiques par timezone
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1, mb: 2 }}>
                <Typography variant="h6">Total abonnés</Typography>
                <Chip label={totalSubscribers} color="primary" size="large" />
              </Box>

              {timezones.map((tz) => (
                <Box
                  key={tz.timezone}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    mb: 1,
                    bgcolor: selectedTimezone === tz.timezone ? 'action.selected' : 'grey.50',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: selectedTimezone === tz.timezone ? '2px solid' : '1px solid',
                    borderColor: selectedTimezone === tz.timezone ? 'primary.main' : 'grey.200',
                  }}
                  onClick={() => setSelectedTimezone(tz.timezone)}
                >
                  <Typography variant="body1" fontWeight={selectedTimezone === tz.timezone ? 600 : 400}>
                    {tz.timezone}
                  </Typography>
                  <Chip 
                    label={`${tz.count} abonnés`} 
                    color={selectedTimezone === tz.timezone ? 'primary' : 'default'}
                    size="small" 
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Historique des envois */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">📅 Historique des envois (7 derniers jours)</Typography>
                <Tooltip title="Actualiser">
                  <IconButton onClick={loadHistory} size="small" disabled={loadingHistory}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {loadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : Object.keys(history).length === 0 ? (
                <Alert severity="info">Aucun historique disponible</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Date</strong></TableCell>
                        {timezones.map(tz => (
                          <TableCell key={tz.timezone} align="center">
                            <strong>{tz.timezone.split('/')[1]}</strong>
                          </TableCell>
                        ))}
                        <TableCell align="center"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(history).sort().reverse().map((date) => {
                        const dayTotal = Object.values(history[date]).reduce((a, b) => a + b, 0);
                        const isToday = date === new Date().toISOString().split('T')[0];
                        return (
                          <TableRow key={date} sx={{ bgcolor: isToday ? 'action.hover' : 'inherit' }}>
                            <TableCell>
                              <strong>{date}</strong>
                              {isToday && <Chip label="Aujourd'hui" size="small" color="primary" sx={{ ml: 1 }} />}
                            </TableCell>
                            {timezones.map(tz => (
                              <TableCell key={tz.timezone} align="center">
                                {history[date][tz.timezone] ? (
                                  <Chip 
                                    label={history[date][tz.timezone]} 
                                    size="small" 
                                    color={history[date][tz.timezone] === tz.count ? 'success' : 'warning'}
                                  />
                                ) : (
                                  <Typography color="text.disabled">-</Typography>
                                )}
                              </TableCell>
                            ))}
                            <TableCell align="center">
                              <Chip 
                                label={dayTotal} 
                                size="small" 
                                color={dayTotal === totalSubscribers ? 'success' : 'warning'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminResend;
